/**
 * The airship's weather field (ADR 0033, lifted from brad-airship
 * `airship-weather.js`): one 256² tileable noise texture, built once per ship
 * scene on the CPU, feeds both the drifting cloud shadows on the deck's
 * materials (`cloudShadowHook`) and the procedural sky (`sky.ts`). The CPU
 * keeps the bytes so it samples exactly what the GPU sees (the sun passing
 * behind a cloud at the ship).
 *
 * Channels: R soft fbm, 4 cells per tile (big cloud masses); G soft fbm, 8
 * cells, another seed (detail); B ridged fbm (mountain and island profiles);
 * A billow fbm, 16 cells (cumulus puffs).
 *
 * `createWeather` is the weather clock: each frame it drifts the cloud field
 * downwind (in flight the ship's own motion streams it astern) and returns
 * the sunlight at the ship's centre (1 sunny, down to 1 − strength shaded).
 */
import * as THREE from 'three';
import type { CloudUniforms } from './hooks.ts';

const SIZE = 256;

const lattice = (period: number, seed: number): ((x: number, y: number) => number) => {
  const v = new Float32Array(period * period);
  let s = seed >>> 0;
  for (let i = 0; i < v.length; i += 1) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    v[i] = s / 4294967296;
  }
  const at = (i: number, j: number): number => v[(((j % period) + period) % period) * period + (((i % period) + period) % period)] ?? 0;
  return (x, y) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;
    const u = fx * fx * (3 - 2 * fx);
    const w = fy * fy * (3 - 2 * fy);
    const a = at(xi, yi);
    const b = at(xi + 1, yi);
    const c = at(xi, yi + 1);
    const d = at(xi + 1, yi + 1);
    return a + (b - a) * u + (c - a) * w + (a - b - c + d) * u * w;
  };
};

const fbm = (base: number, octaves: number, seed: number, shape: (n: number) => number = (n) => n): ((x: number, y: number) => number) => {
  const layers = Array.from({ length: octaves }, (_, o) => lattice(base << o, seed + o * 7919));
  return (x, y) => {
    let sum = 0;
    let amp = 0.5;
    let norm = 0;
    layers.forEach((layer, o) => {
      const f = (base << o) / SIZE;
      sum += shape(layer(x * f, y * f)) * amp;
      norm += amp;
      amp *= 0.5;
    });
    return sum / norm;
  };
};

export type WeatherNoise = { readonly texture: THREE.DataTexture; readonly data: Uint8Array };

/** The noise texture (about 256 KiB); its owner disposes it. */
export const buildWeatherNoise = (): WeatherNoise => {
  const data = new Uint8Array(SIZE * SIZE * 4);
  const r = fbm(4, 4, 11);
  const g = fbm(8, 4, 97);
  const b = fbm(4, 5, 311, (n) => 1 - Math.abs(n * 2 - 1));
  const a = fbm(16, 3, 733, (n) => Math.abs(n * 2 - 1));
  // Stretch contrast so thresholds behave predictably (fbm clusters near .5).
  const c = (v: number, k: number): number => Math.max(0, Math.min(255, Math.round((0.5 + (v - 0.5) * k) * 255)));
  for (let y = 0; y < SIZE; y += 1) {
    for (let x = 0; x < SIZE; x += 1) {
      const i = (y * SIZE + x) * 4;
      data[i] = c(r(x, y), 1.9);
      data[i + 1] = c(g(x, y), 1.9);
      data[i + 2] = c(b(x, y), 1.4);
      data[i + 3] = c(a(x, y), 1.5);
    }
  }
  const texture = new THREE.DataTexture(data, SIZE, SIZE);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return { texture, data };
};

/** Bilinear CPU sample of one channel, matching the GPU's linear filter and repeat. */
export const sampleNoise = (noise: WeatherNoise, u: number, v: number, channel = 0): number => {
  const x = u * SIZE - 0.5;
  const y = v * SIZE - 0.5;
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const d = noise.data;
  const at = (i: number, j: number): number => (d[((((j % SIZE) + SIZE) % SIZE) * SIZE + (((i % SIZE) + SIZE) % SIZE)) * 4 + channel] ?? 0) / 255;
  const a = at(xi, yi);
  const b = at(xi + 1, yi);
  const c = at(xi, yi + 1);
  const e = at(xi + 1, yi + 1);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + e) * fx * fy;
};

/** The CPU twin of the hook's `airshipCloudShadow` (1 = full sun). */
export const cloudShadowAt = (noise: WeatherNoise, u: CloudUniforms, x: number, y: number, z: number): number => {
  const p = u.csParams.value;
  const o = u.csOffset.value;
  const s = u.csShift.value;
  const uu = (x + y * s.x) * p.z + o.x;
  const vv = (z + y * s.y) * p.z + o.y;
  const n = sampleNoise(noise, uu, vv, 0) * 0.78 + sampleNoise(noise, uu * 1.7 + 0.37, vv * 1.7 + 0.11, 1) * 0.22;
  return 1 - p.x * THREE.MathUtils.smoothstep(n, p.y - p.w, p.y + p.w);
};

/** What the weather reads each frame: the time of day's wind, its direction (three's xz), the cloud shadow's strength, the flight's blend and the sun. */
export type WeatherInput = { readonly wind: number; readonly windDir: THREE.Vector2; readonly cloudShadow: number; readonly flight: number; readonly sunDirection: THREE.Vector3 };

export type Weather = { readonly sunAtShip: () => number; update(dt: number, input: WeatherInput): number };

/** The weather clock over the ship's centre (41, 2, 0). */
export const createWeather = (noise: WeatherNoise, u: CloudUniforms, centre: THREE.Vector3 = new THREE.Vector3(41, 2, 0)): Weather => {
  const coverage = 0.6;
  const drift = new THREE.Vector2();
  let sun = 1;
  u.csNoise.value = noise.texture;
  return {
    sunAtShip: () => sun,
    update(dt, ts) {
      const p = u.csParams.value;
      p.x = ts.cloudShadow;
      // Big slow shapes; coverage leaves the ship mostly sunlit between passes.
      p.y = coverage;
      p.w = 0.05;
      // The cloud field moves with the wind; flying adds the ship's own motion (-x).
      drift.set(ts.windDir.x * (1.6 + ts.wind * 2.6) - ts.flight * 16, ts.windDir.y * (1.6 + ts.wind * 2.6));
      u.csOffset.value.addScaledVector(drift, -dt * p.z);
      u.csOffset.value.set(u.csOffset.value.x % 64, u.csOffset.value.y % 64);
      const s = ts.sunDirection;
      u.csShift.value.set(s.x / Math.max(s.y, 0.12), s.z / Math.max(s.y, 0.12));
      sun = cloudShadowAt(noise, u, centre.x, centre.y, centre.z);
      return sun;
    },
  };
};
